import type { FC } from 'react'
import type { HistoryItem, SceneProps } from '@/components/landing/games/scene-ui'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { SceneLabel } from '@/components/landing/games/scene-ui'

type PocketColor = 'green' | 'red' | 'black'

const ORDER = [0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26]
const RED = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36])
const STEP = 360 / ORDER.length
const SPIN_RADIUS = 91
const REST_RADIUS = 68
const SPIN_MS = 4800
const REST_MS = 1700

const FILLS: Record<PocketColor, string> = {
    green: '#1FA36C',
    red: '#FF5B1F',
    black: '#17171C'
}

const SWATCHES: Record<PocketColor, string> = {
    green: 'bg-[#1FA36C] text-ink',
    red: 'bg-ember text-ink',
    black: 'bg-[#25252c] text-bone ring-1 ring-inset ring-white/15'
}

const colorOf = (number: number): PocketColor => (number === 0 ? 'green' : RED.has(number) ? 'red' : 'black')

const polar = (angle: number, radius: number): [number, number] => {
    const radians = (angle * Math.PI) / 180
    return [100 + radius * Math.sin(radians), 100 - radius * Math.cos(radians)]
}

const fixed = (value: number): string => value.toFixed(2)

const slice = (index: number, outer: number, inner: number): string => {
    const start = index * STEP - STEP / 2
    const end = index * STEP + STEP / 2
    const [x1, y1] = polar(start, outer)
    const [x2, y2] = polar(end, outer)
    const [x3, y3] = polar(end, inner)
    const [x4, y4] = polar(start, inner)

    return `M ${fixed(x1)} ${fixed(y1)} A ${outer} ${outer} 0 0 1 ${fixed(x2)} ${fixed(y2)} L ${fixed(x3)} ${fixed(y3)} A ${inner} ${inner} 0 0 0 ${fixed(x4)} ${fixed(y4)} Z`
}

const POCKETS = ORDER.map((number, index) => {
    const [tx, ty] = polar(index * STEP, 80)

    return {
        number,
        band: slice(index, 86, 74),
        pocket: slice(index, 74, 62),
        tx: fixed(tx),
        ty: fixed(ty),
        angle: fixed(index * STEP),
        fill: FILLS[colorOf(number)]
    }
})

const POSTER_NUMBER = 17
const POSTER_REL = ORDER.indexOf(POSTER_NUMBER) * STEP
const POSTER_BALL = polar(POSTER_REL, REST_RADIUS)

const POSTER_HISTORY: HistoryItem[] = [
    { id: 5, value: 32 },
    { id: 4, value: 0 },
    { id: 3, value: 11 },
    { id: 2, value: 26 },
    { id: 1, value: 5 }
]

const RouletteScene: FC<SceneProps> = ({ active }) => {
    const wheelRef = useRef<SVGGElement>(null)
    const ballRef = useRef<SVGCircleElement>(null)
    const motion = useRef({ wheel: 0, rel: POSTER_REL, radius: REST_RADIUS })
    const [result, setResult] = useState<number | null>(POSTER_NUMBER)
    const [history, setHistory] = useState<HistoryItem[]>(POSTER_HISTORY)
    const nextId = useRef(6)

    useEffect(() => {
        const wheel = wheelRef.current
        const ball = ballRef.current

        if (!active || !wheel || !ball) return

        const state = motion.current
        let frame = 0
        let last = 0
        let alive = true
        let mode: 'rest' | 'spin' = 'rest'
        let phaseStart = performance.now() - REST_MS * 0.6
        let launchRel = state.rel
        let targetRel = state.rel
        let targetIndex = 0

        const place = () => {
            const [bx, by] = polar(state.wheel + state.rel, state.radius)
            wheel.setAttribute('transform', `rotate(${state.wheel.toFixed(2)} 100 100)`)
            ball.setAttribute('cx', bx.toFixed(2))
            ball.setAttribute('cy', by.toFixed(2))
        }

        const beginSpin = (now: number) => {
            targetIndex = Math.floor(Math.random() * ORDER.length)
            launchRel = state.rel

            let target = targetIndex * STEP
            while (launchRel - target < 900) target -= 360

            targetRel = target
            mode = 'spin'
            phaseStart = now
            setResult(null)
        }

        const step = (now: number) => {
            if (!alive) return

            const dt = last ? Math.min(50, now - last) / 1000 : 0
            last = now
            state.wheel = (state.wheel + 26 * dt) % 360

            if (mode === 'spin') {
                const u = Math.min(1, (now - phaseStart) / SPIN_MS)
                const eased = 1 - Math.pow(1 - u, 3)
                state.rel = launchRel + (targetRel - launchRel) * eased

                if (u < 0.55) {
                    state.radius = SPIN_RADIUS
                }
                else {
                    const v = Math.min(1, (u - 0.55) / 0.3)
                    state.radius = SPIN_RADIUS + (REST_RADIUS - SPIN_RADIUS) * v + Math.sin(v * Math.PI * 3) * (1 - v) * 2.5
                }

                if (u >= 1) {
                    const number = ORDER[targetIndex]
                    const id = nextId.current
                    nextId.current += 1

                    state.rel = targetRel % 360
                    state.radius = REST_RADIUS
                    mode = 'rest'
                    phaseStart = now
                    setResult(number)
                    setHistory(previous => [{ id, value: number }, ...previous].slice(0, 5))
                }
            }
            else if (now - phaseStart > REST_MS) {
                beginSpin(now)
            }

            place()
            frame = requestAnimationFrame(step)
        }

        frame = requestAnimationFrame(step)

        return () => {
            alive = false
            cancelAnimationFrame(frame)
        }
    }, [active])

    const color = result === null ? null : colorOf(result)

    return (
        <div className='absolute inset-0 flex items-center justify-center gap-6 px-6 pb-6 pt-14'>
            <div className='relative aspect-square h-full max-h-[236px]'>
                <div aria-hidden='true' className='absolute inset-[8%] rounded-full bg-ember/10 blur-2xl' />

                <svg viewBox='0 0 200 200' role='img' aria-label={result === null ? 'Roulette wheel spinning' : `Roulette wheel, ball in ${result}`} className='relative size-full'>
                    <defs>
                        <radialGradient id='roulette-cone' cx='50%' cy='42%' r='62%'>
                            <stop offset='0%' stopColor='#2c2c33' />
                            <stop offset='100%' stopColor='#0b0b0e' />
                        </radialGradient>
                    </defs>

                    <circle cx='100' cy='100' r='98' fill='#0c0c0f' stroke='rgba(255, 255, 255, 0.1)' />
                    <circle cx='100' cy='100' r='86.5' fill='#131317' />

                    <g ref={wheelRef} transform='rotate(0 100 100)'>
                        {POCKETS.map(pocket => (
                            <path key={`band-${pocket.number}`} d={pocket.band} fill={pocket.fill} stroke='rgba(8, 8, 10, 0.7)' strokeWidth='0.6' />
                        ))}

                        {POCKETS.map(pocket => (
                            <path key={`pocket-${pocket.number}`} d={pocket.pocket} fill='#0e0e12' stroke='rgba(255, 255, 255, 0.08)' strokeWidth='0.5' />
                        ))}

                        {POCKETS.map(pocket => (
                            <text
                                key={`label-${pocket.number}`}
                                x={pocket.tx}
                                y={pocket.ty}
                                transform={`rotate(${pocket.angle} ${pocket.tx} ${pocket.ty})`}
                                textAnchor='middle'
                                dominantBaseline='central'
                                fontSize='6'
                                className='fill-bone font-mono'
                            >
                                {pocket.number}
                            </text>
                        ))}

                        <circle cx='100' cy='100' r='62' fill='url(#roulette-cone)' stroke='rgba(255, 255, 255, 0.08)' />
                        <path d='M 100 58 V 142 M 58 100 H 142' stroke='rgba(244, 242, 238, 0.16)' strokeWidth='3' strokeLinecap='round' />
                        <circle cx='100' cy='100' r='13' fill='#1d1d22' stroke='rgba(244, 242, 238, 0.24)' />
                        <circle cx='100' cy='100' r='4' fill='#F4F2EE' />
                    </g>

                    <circle ref={ballRef} cx={fixed(POSTER_BALL[0])} cy={fixed(POSTER_BALL[1])} r='3.8' fill='#FFFFFF' stroke='rgba(0, 0, 0, 0.35)' strokeWidth='0.6' />
                </svg>
            </div>

            <div className='hidden w-[112px] shrink-0 flex-col gap-4 @sm:flex'>
                <div>
                    <SceneLabel>Result</SceneLabel>
                    <div className='mt-2 flex items-center gap-2.5'>
                        <span className={cn('grid size-10 place-items-center rounded-xl font-mono text-sm font-semibold transition-colors duration-300', color ? SWATCHES[color] : 'bg-white/[0.04] text-steel')}>
                            {result ?? '··'}
                        </span>
                        <span className='font-mono text-[10px] uppercase tracking-[0.12em] text-steel'>
                            {color ?? 'Spinning'}
                        </span>
                    </div>
                </div>

                <div>
                    <SceneLabel>Last spins</SceneLabel>
                    <div className='mt-2 flex flex-wrap gap-1'>
                        {history.map(item => (
                            <span
                                key={item.id}
                                className={cn('grid size-6 animate-in fade-in zoom-in-75 place-items-center rounded-md font-mono text-[9px] duration-300', SWATCHES[colorOf(item.value)])}
                            >
                                {item.value}
                            </span>
                        ))}
                    </div>
                </div>

                <SceneLabel className='leading-relaxed'>European, single zero</SceneLabel>
            </div>
        </div>
    )
}

export default RouletteScene
